import { GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { MiniGameService } from "../services/MiniGameService";

const styles: string = css`
    .bg {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover; 
        z-index: -1; 
    }
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: white;
    }
    .title { 
        font-size: 2rem;
        font-weight: bold;
        margin-bottom: 20px;
    }
    .chest {
    position: fixed;
    width: 900px; 
    height: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1; 
    opacity: 0.8; 
}
    .lock {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #8B4513;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
}
    .cd {
    display: flex;
    justify-content: center;
    gap: 10px;
    background: #fff;
    padding: 10px;
    border-radius: 5px;
    font-size: 24px;
    font-weight: bold;
    width: 100px;
    text-align: center;
    color: black;
}
    .pad {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 5px;
                margin-top: 10px;
}
    .pad button {
                width: 50px;
                height: 50px;
                font-size: 20px;
                border: none;
                background-color: #d4a373;
                color: white;
                border-radius: 5px;
                cursor: pointer;
                transition: background 0.3s;
}
    .pad button:hover {
    background-color: #a66a33;
}
    #codeChest {
  color: #5e3d26; 
  font-size: 10px;
  font-family: "Press Start 2P", monospace; 
  opacity: 0.6; 
}
`;

export class MiniGameComponent extends HTMLElement {
    /** Instance of the game event service */
    private _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private _gameRouteService: GameRouteService = new GameRouteService();

    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();
    private _miniGameService: MiniGameService = new MiniGameService();

    /**
    * The "constructor" of a Web Component
    */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
        this.shadowRoot?.addEventListener("click", event => {
            const target: HTMLElement = event.target as HTMLElement;

            if (target.id === "closeButton") {
                this._gameEventService.switchPage(Page.Canvas);
            }
        });
    }

    /**
    * Refresh the current game state
    */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
        * Update the canvas to the provided game state
        *
        * @param state Game state to update the canvas to
        */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    private render(): void {
        if (!this.shadowRoot) return;

        const elements: HTMLElement[] = htmlArray`
            <style>
            ${styles}           
            </style>
            <img src="./assets/img/rooms/background.png" class="bg">
            <img src="/assets/img/items/chest.png" class="chest">
            ${this.renderTitle()}
            ${this.renderContent()} 
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    private renderContent(): HTMLElement {
        const codeString: string | null = localStorage.getItem("codeToChest");
        const element: HTMLElement = html`
        <div>
            <style>
                ${this.style}
            </style>
            
            <div class="lock">
                <div class="cd">
                    <div>.</div>
                    <div>.</div>
                    <div>.</div>
                    <div>.</div>
                </div>
                
                <div class="pad">
                    <div><button>7</button></div>
                    <div><button>8</button></div>
                    <div><button>9</button></div>
                    <div><button>4</button></div>
                    <div><button>5</button></div>
                    <div><button>6</button></div>
                    <div><button>1</button></div>
                    <div><button>2</button></div>
                    <div><button>3</button></div>
                     <div><button></button></div>
                    <div><button>0</button></div>
                     <div><button></button></div>
                    <div><button>x</button></div>
                    <div><button>&lt;</button></div>
                    <div><button>OK</button></div>
                </div>
                <div id="codeChest">code is: ${codeString}</div>
            </div>
        </div>
        `;

        const cdNodes: NodeListOf<HTMLElement> = element.querySelectorAll(".cd > div");
        let currentKey: number = 0;

        element.querySelectorAll("button").forEach(e => {
            const key: string = e.innerText;

            e.addEventListener("click", async () => {
                if (key === "<") {
                    currentKey = Math.max(0, currentKey - 1);
                    cdNodes[currentKey].innerText = ".";
                }
                else if (key === "x") {
                    this._gameEventService.switchPage(Page.Canvas);
                }
                else if (key === "OK") {
                    if (currentKey < 4) return;

                    cdNodes.forEach(f => f.style.display = "none");

                    let result: string = "";

                    if (currentKey > 3) {
                        for (let i: number = 0; i < 4; i++) {
                            result += cdNodes[i].innerText;
                        }
                    }

                    const codeCorrect: boolean = await this._miniGameService.checkCode(result);

                    if (codeCorrect) {
                        cdNodes[0].style.display = "block";
                        cdNodes[0].innerText = "CORRECT";
                    }
                    else {
                        cdNodes[0].style.display = "block";
                        cdNodes[0].innerText = "WRONG";
                    }
                }
                else {
                    if (currentKey > 3) return;

                    cdNodes[currentKey].innerText = key;
                    currentKey++;
                }
            });
        });

        return element;
    }

    private renderTitle(): string {
        return "<div class='title'>Mini Game</div>";
    }
}
